import {KeyboardArrowDown} from '@mui/icons-material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import {styled} from '@mui/material/styles';
import {forwardRef, Fragment, KeyboardEventHandler, MouseEvent, ReactElement, useState} from 'react';
import {MenuItem} from '../Menu/MenuItem';
import {MenuList} from '../Menu/MenuList';
import {NavItemButton, NavItemButtonProps} from '../Nav/NavItemButton';
import {Paper} from '../Paper/Paper';
import {NavigationLinkOptions} from './types';

export interface NavItemDropdownProps extends NavItemButtonProps {
    /**
     * Navigation items prop.
     * @default [];
     */
    items?: NavigationLinkOptions[];
}

const StyledDropdownMenu = styled(Popper)({
    position: 'absolute',
    zIndex: 1300
});

export const NavItemDropdown = forwardRef<HTMLDivElement, NavItemDropdownProps>(({
    children, items, ...props
}, ref) => {
    const hasItems = Array.isArray(items) && items.length > 0;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const handleListKeyDown: KeyboardEventHandler = (event) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setAnchorEl(null);
        }

        if (event.key === 'Escape') {
            setAnchorEl(null);
        }
    };

    const handleItemClick = (event: MouseEvent, item: NavigationLinkOptions): void => {
        // run onclick if function exists
        if (item.onClick) {
            item.onClick(event);
        }

        // if item doesn't have subNavigation items
        if (!Array.isArray(item.subNavigationItems)) {
            handleClose();
        }
    };

    return (
        <Fragment>
            <NavItemButton
                endIcon={hasItems && <KeyboardArrowDown/>}
                onClick={handleClick}
                {...props}
            >
                {children}
            </NavItemButton>
            {/* FIXME: Duplicated code, should be extracted into 'Menu' component */}
            {hasItems && (
                <StyledDropdownMenu
                    anchorEl={anchorEl}
                    disablePortal
                    modifiers={[
                        {
                            name: 'flip',
                            enabled: true,
                            options: {
                                altBoundary: true,
                                rootBoundary: 'viewport',
                                padding: 8
                            }
                        },
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                altAxis: true,
                                altBoundary: true,
                                tether: true,
                                rootBoundary: 'viewport',
                                padding: 8
                            }
                        }
                    ]} // taken from DropdownMenu.tsx
                    open={open}
                    placement={'bottom-start'}
                    ref={ref}
                    role={undefined}
                    transition
                >
                    {({TransitionProps}): ReactElement => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: 'left top'
                            }}
                        >
                            <Paper borderRadius='md' >
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MenuList
                                        autoFocusItem={open}
                                        onKeyDown={handleListKeyDown}
                                    >
                                        {items.map((item, index) => (
                                            <MenuItem
                                                {...item}
                                                key={index}
                                                onClick={(event: MouseEvent): void => {
                                                    handleItemClick(event, item);
                                                }}
                                            >
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </StyledDropdownMenu>
            )}
        </Fragment>
    );
});

NavItemDropdown.displayName = 'NavItemDropdown';
